/* ==========================================================================
   Relationship System - The Arcanopolis Archive
   Interactive relationship network visualization using vis-network
   ========================================================================== */

const RelationshipSystem = {
    network: null,
    nodes: [],
    edges: [],
    
    // Initialize relationship system
    init() {
        this.setupNetworkInterface();
        this.loadRelationshipData();
        this.createNetwork();
        this.setupEventHandlers();
    },
    
    // Setup network interface
    setupNetworkInterface() {
        // Network container
        const networkContainer = document.getElementById('networkContainer');
        if (!networkContainer) {
            console.error('Network container not found');
            return;
        }
        
        // Create network element
        networkContainer.innerHTML = '<div id="network"></div>';
    },
    
    // Load relationship data from content
    loadRelationshipData() {
        const allItems = [];
        for (const [category, items] of App.data) {
            allItems.push(...items.map(item => ({ ...item, category })));
        }
        
        // Create nodes from all content items
        this.nodes = allItems.map(item => ({
            id: item.id,
            label: item.title,
            group: item.category,
            title: this.createNodeTooltip(item),
            color: this.getNodeColor(item.category),
            size: this.getNodeSize(item),
            shape: this.getNodeShape(item.category),
            data: item
        }));
        
        // Create edges from relationships
        this.edges = [];
        allItems.forEach(item => {
            if (item.relationships && Array.isArray(item.relationships)) {
                item.relationships.forEach(relationship => {
                    const targetItem = allItems.find(target => 
                        target.title === relationship.target || target.id === relationship.target
                    );
                    
                    if (targetItem) {
                        this.edges.push({
                            from: item.id,
                            to: targetItem.id,
                            label: relationship.type,
                            color: this.getEdgeColor(relationship.type),
                            width: this.getEdgeWidth(relationship.type),
                            dashes: this.getEdgeDashes(relationship.type),
                            title: `${item.title} → ${targetItem.title}: ${relationship.type}`
                        });
                    }
                });
            }
            
            // Add cross-reference relationships
            if (item.crossReferences && Array.isArray(item.crossReferences)) {
                item.crossReferences.forEach(refTitle => {
                    const refItem = allItems.find(target => 
                        target.title === refTitle || target.id === refTitle
                    );
                    
                    if (refItem) {
                        // Check if edge already exists
                        const existingEdge = this.edges.find(edge => 
                            (edge.from === item.id && edge.to === refItem.id) ||
                            (edge.from === refItem.id && edge.to === item.id)
                        );
                        
                        if (!existingEdge) {
                            this.edges.push({
                                from: item.id,
                                to: refItem.id,
                                label: 'references',
                                color: this.getEdgeColor('references'),
                                width: this.getEdgeWidth('references'),
                                dashes: this.getEdgeDashes('references'),
                                title: `${item.title} references ${refItem.title}`
                            });
                        }
                    }
                });
            }
        });
        
        console.log(`🕸️ Loaded ${this.nodes.length} nodes and ${this.edges.length} edges`);
    },
    
    // Create node tooltip
    createNodeTooltip(item) {
        let tooltip = `<strong>${item.title}</strong>`;
        
        if (item.category) {
            tooltip += `<br><em>${App.getCategoryLabel(item.category)}</em>`;
        }
        
        if (item.type) {
            tooltip += `<br>Type: ${item.type}`;
        }
        
        if (item.tags && item.tags.length > 0) {
            tooltip += `<br>Tags: ${item.tags.join(', ')}`;
        }
        
        if (item.location) {
            tooltip += `<br>Location: ${item.location}`;
        }
        
        if (item.faction) {
            tooltip += `<br>Faction: ${item.faction}`;
        }
        
        return tooltip;
    },
    
    // Get node color based on category
    getNodeColor(category) {
        const colors = {
            'lore': '#8B4513',        // Brown
            'locations': '#228B22',   // Forest Green
            'npcs': '#4169E1',        // Royal Blue
            'factions': '#DC143C',    // Crimson
            'items': '#FFD700',       // Gold
            'handouts': '#9370DB',    // Medium Purple
            'containment': '#FF6347', // Tomato
            'additional': '#20B2AA'   // Light Sea Green
        };
        
        return colors[category] || '#808080';
    },
    
    // Get node size based on content importance
    getNodeSize(item) {
        let size = 20; // Base size
        
        // Increase size for important content
        if (item.tags && item.tags.includes('important')) {
            size += 10;
        }
        
        if (item.type === 'major') {
            size += 15;
        }
        
        // Increase size based on number of relationships
        const relationshipCount = this.edges.filter(edge => 
            edge.from === item.id || edge.to === item.id
        ).length;
        
        size += Math.min(relationshipCount * 2, 20);
        
        return Math.max(size, 15);
    },
    
    // Get node shape based on category
    getNodeShape(category) {
        const shapes = {
            'lore': 'diamond',
            'locations': 'box',
            'npcs': 'ellipse',
            'factions': 'triangle',
            'items': 'star',
            'handouts': 'square',
            'containment': 'triangleDown',
            'additional': 'circle'
        };
        
        return shapes[category] || 'circle';
    },
    
    // Get edge color based on relationship type
    getEdgeColor(relationshipType) {
        const colors = {
            'allied': '#00FF00',      // Green
            'rival': '#FF0000',       // Red
            'member': '#0000FF',      // Blue
            'located': '#FFA500',     // Orange
            'references': '#800080',  // Purple
            'related': '#008080',     // Teal
            'enemy': '#8B0000',       // Dark Red
            'friend': '#32CD32',      // Lime Green
            'family': '#FF69B4',      // Hot Pink
            'colleague': '#4682B4'    // Steel Blue
        };
        
        return colors[relationshipType] || '#808080';
    },
    
    // Get edge width based on relationship type
    getEdgeWidth(relationshipType) {
        const widths = {
            'allied': 3,
            'rival': 2,
            'member': 2,
            'located': 1,
            'references': 1,
            'related': 1,
            'enemy': 3,
            'friend': 2,
            'family': 2,
            'colleague': 1
        };
        
        return widths[relationshipType] || 1;
    },
    
    // Get edge dashes based on relationship type
    getEdgeDashes(relationshipType) {
        const dashes = {
            'allied': false,
            'rival': [5, 5],
            'member': false,
            'located': [10, 5],
            'references': [2, 2],
            'related': [5, 5],
            'enemy': [10, 5],
            'friend': false,
            'family': false,
            'colleague': [3, 3]
        };
        
        return dashes[relationshipType] || false;
    },
    
    // Create network visualization
    createNetwork() {
        const networkElement = document.getElementById('network');
        if (!networkElement) {
            console.error('Network element not found');
            return;
        }
        
        // Network data
        const data = {
            nodes: this.nodes,
            edges: this.edges
        };
        
        // Network options
        const options = {
            nodes: {
                borderWidth: 2,
                borderWidthSelected: 4,
                font: {
                    size: 14,
                    color: '#000000'
                },
                shadow: {
                    enabled: true,
                    color: 'rgba(0,0,0,0.2)',
                    size: 10,
                    x: 5,
                    y: 5
                }
            },
            edges: {
                font: {
                    size: 12,
                    color: '#000000',
                    strokeWidth: 3,
                    strokeColor: '#ffffff'
                },
                smooth: {
                    type: 'continuous',
                    forceDirection: 'none',
                    roundness: 0.4
                },
                arrows: {
                    to: {
                        enabled: true,
                        scaleFactor: 0.5
                    }
                }
            },
            physics: {
                enabled: true,
                stabilization: {
                    enabled: true,
                    iterations: 100,
                    updateInterval: 50
                },
                barnesHut: {
                    gravitationalConstant: -2000,
                    centralGravity: 0.3,
                    springLength: 95,
                    springConstant: 0.04,
                    damping: 0.09,
                    avoidOverlap: 0.1
                }
            },
            interaction: {
                hover: true,
                hoverConnectedEdges: true,
                selectConnectedEdges: false,
                tooltipDelay: 300,
                hideEdgesOnDrag: false,
                hideNodesOnDrag: false
            },
            layout: {
                improvedLayout: true,
                clusterThreshold: 150
            }
        };
        
        // Create network
        this.network = new vis.Network(networkElement, data, options);
    },
    
    // Setup event handlers
    setupEventHandlers() {
        if (!this.network) return;
        
        // Node click handler
        this.network.on('click', (params) => {
            if (params.nodes.length > 0) {
                const nodeId = params.nodes[0];
                const node = this.nodes.find(n => n.id === nodeId);
                if (node && node.data) {
                    this.openContent(node.data);
                }
            }
        });
        
        // Double-click to open content
        this.network.on('doubleClick', (params) => {
            if (params.nodes.length > 0) {
                const nodeId = params.nodes[0];
                const node = this.nodes.find(n => n.id === nodeId);
                if (node && node.data) {
                    this.openContent(node.data);
                }
            }
        });
        
        // Setup filter controls
        this.setupFilterControls();
    },
    
    // Setup filter controls
    setupFilterControls() {
        // Category filter
        const categoryFilter = document.getElementById('categoryFilter');
        if (categoryFilter) {
            const categories = [...new Set(this.nodes.map(node => node.group))];
            categoryFilter.innerHTML = '<option value="all">All Categories</option>' +
                categories.map(category => 
                    `<option value="${category}">${App.getCategoryLabel(category)}</option>`
                ).join('');
            
            categoryFilter.addEventListener('change', () => {
                this.filterByCategory(categoryFilter.value);
            });
        }
        
        // Relationship type filter
        const relationshipFilter = document.getElementById('relationshipFilter');
        if (relationshipFilter) {
            const relationshipTypes = [...new Set(this.edges.map(edge => edge.label))];
            relationshipFilter.innerHTML = '<option value="all">All Relationships</option>' +
                relationshipTypes.map(type => 
                    `<option value="${type}">${type}</option>`
                ).join('');
            
            relationshipFilter.addEventListener('change', () => {
                this.filterByRelationshipType(relationshipFilter.value);
            });
        }
        
        // Layout controls
        this.setupLayoutControls();
    },
    
    // Setup layout controls
    setupLayoutControls() {
        const layoutSelect = document.getElementById('layoutSelect');
        if (layoutSelect) {
            layoutSelect.addEventListener('change', () => {
                this.changeLayout(layoutSelect.value);
            });
        }
        
        // Physics toggle
        const physicsToggle = document.getElementById('physicsToggle');
        if (physicsToggle) {
            physicsToggle.addEventListener('change', () => {
                this.togglePhysics(physicsToggle.checked);
            });
        }
        
        // Stabilize button
        const stabilizeBtn = document.getElementById('stabilizeBtn');
        if (stabilizeBtn) {
            stabilizeBtn.addEventListener('click', () => {
                this.stabilizeNetwork();
            });
        }
    },
    
    // Filter by category
    filterByCategory(category) {
        if (category === 'all') {
            this.network.setData({ nodes: this.nodes, edges: this.edges });
        } else {
            const filteredNodes = this.nodes.filter(node => node.group === category);
            const filteredEdges = this.edges.filter(edge => 
                filteredNodes.some(node => node.id === edge.from || node.id === edge.to)
            );
            
            this.network.setData({ nodes: filteredNodes, edges: filteredEdges });
        }
    },
    
    // Filter by relationship type
    filterByRelationshipType(relationshipType) {
        if (relationshipType === 'all') {
            this.network.setData({ nodes: this.nodes, edges: this.edges });
        } else {
            const filteredEdges = this.edges.filter(edge => edge.label === relationshipType);
            const connectedNodes = new Set();
            
            filteredEdges.forEach(edge => {
                connectedNodes.add(edge.from);
                connectedNodes.add(edge.to);
            });
            
            const filteredNodes = this.nodes.filter(node => connectedNodes.has(node.id));
            
            this.network.setData({ nodes: filteredNodes, edges: filteredEdges });
        }
    },
    
    // Change layout
    changeLayout(layoutType) {
        const options = this.network.getOptions();
        
        switch (layoutType) {
            case 'hierarchical':
                options.layout = {
                    hierarchical: {
                        enabled: true,
                        direction: 'UD',
                        sortMethod: 'directed',
                        nodeSpacing: 200,
                        levelSeparation: 150
                    }
                };
                break;
            case 'circular':
                options.layout = {
                    randomSeed: 2
                };
                break;
            case 'force':
                options.layout = {
                    improvedLayout: true
                };
                break;
        }
        
        this.network.setOptions(options);
    },
    
    // Toggle physics
    togglePhysics(enabled) {
        const options = this.network.getOptions();
        options.physics.enabled = enabled;
        this.network.setOptions(options);
    },
    
    // Stabilize network
    stabilizeNetwork() {
        this.network.stabilize();
    },
    
    // Open content in new page
    openContent(item) {
        window.open(`content.html?id=${item.id}`, '_blank');
    },
    
    // Update network with new data
    updateNetwork() {
        this.loadRelationshipData();
        if (this.network) {
            this.network.setData({ nodes: this.nodes, edges: this.edges });
        }
    },
    
    // Get network statistics
    getNetworkStats() {
        const stats = {
            totalNodes: this.nodes.length,
            totalEdges: this.edges.length,
            categories: {},
            relationshipTypes: {},
            connectedComponents: 0
        };
        
        // Count by category
        this.nodes.forEach(node => {
            stats.categories[node.group] = (stats.categories[node.group] || 0) + 1;
        });
        
        // Count by relationship type
        this.edges.forEach(edge => {
            stats.relationshipTypes[edge.label] = (stats.relationshipTypes[edge.label] || 0) + 1;
        });
        
        // Calculate connected components (simplified)
        const visited = new Set();
        let components = 0;
        
        this.nodes.forEach(node => {
            if (!visited.has(node.id)) {
                components++;
                this.dfs(node.id, visited);
            }
        });
        
        stats.connectedComponents = components;
        
        return stats;
    },
    
    // Depth-first search for connected components
    dfs(nodeId, visited) {
        visited.add(nodeId);
        
        this.edges.forEach(edge => {
            if (edge.from === nodeId && !visited.has(edge.to)) {
                this.dfs(edge.to, visited);
            } else if (edge.to === nodeId && !visited.has(edge.from)) {
                this.dfs(edge.from, visited);
            }
        });
    }
};

// Export for use in other modules
window.RelationshipSystem = RelationshipSystem;